import React, { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';

import DateFnsUtils from '@date-io/date-fns';
import {
  Box,
  Button,
  FormControl,
  Grid,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import deLocale from 'date-fns/locale/es';

import { ReactComponent as CalendarIcon } from '../../../../assets/img/calendar.svg';
import WhatsappSrc from '../../../../assets/img/whatsapp.svg';
// import WhatsappSrc from '../../../../assets/img/whatsapp.png';
import SubHeader from '../../../../components/Header/SubHeader.component';
import Loader from '../../../../components/LoaderSplash/Loader.component';
import { NumberFormatCustom } from '../../../../components/NumberFormartCustom/NumberFormatCustom.component';
import SharedDialog from '../../../../components/SharedDialog/SharedDialog.component';
import { appConfig } from '../../../../config/app';
import { CustomEvents } from '../../../../constants/analytics.constant';
import { useAuthDataContext } from '../../../../context/auth/auth.provider';
import { useEndorsementsDataContext } from '../../../../context/endorsements/endorsements.provider';
import { dataLayerPush } from '../../../../helpers/dataLayerPush';
import useForm from '../../../../helpers/useForm';
import useLogEventInAnalytics from '../../../../hooks/useLogAnalyticsEvent';
import { useTipification } from '../../../../hooks/useTipification';
import {
  EDIT_PAYMET_METHOD,
  getPaymentMethodQuery,
} from '../../../../query/endorsments/EditPaymentMethod/EditPaymentMethod';
import {
  confirmPaymentMethods,
  validateCbuNumber,
  validateCreditCardNumber,
} from '../../../../services/payments/payments.service';
import EditPaymentMethodValidation from './EditPaymentMethod.validations';

const EditPaymentMethod = () => {
  const { endorsement_body } = useEndorsementsDataContext();
  const history = useHistory();
  const [dialog, setDialog] = useState(false);
  const [onModalSuccess, setOnModalSuccess] = useState(false);
  const [onRetryModal, setOnRetryModal] = useState(false);
  const [notAllowUserModal, setNotAllowUserModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [errorCbu, setErrorCbu] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [cbuValidated, setCbuValidated] = useState<any>({});
  const [creditCardValidated, setCreditCardValidated] = useState<any>({});
  const [notValid, setNotValid] = useState(false);
  const [isCreatingEndorsement, setIsCreatingEndorsement] = useState(false);
  const [skip, setSkip] = useState(true);
  const { user } = useAuthDataContext();
  const { logEvent } = useLogEventInAnalytics();
  const { trackEvent, events } = useTipification();

  const logAnalytics = async (eventName: string) => {
    await logEvent(CustomEvents[eventName], {
      app: appConfig?.NAME,
    });
  };

  const {
    data: paymentMethodsList,
    isLoading: paymentMethodsListLoading,
    error: paymentMethodsListError,
  } = useQuery(
    [EDIT_PAYMET_METHOD, { policy_id: endorsement_body.policySelected?.id }],
    getPaymentMethodQuery,
    {
      refetchOnWindowFocus: false,
      enabled: skip,
      retry: false,
      onError: (err: any) => {
        if (err?.response?.status === 406 || err?.response?.status === 409) {
          setNotAllowUserModal(true);
        } else {
          setOnRetryModal(true);
        }
      },
    }
  );

  const EditPaymentMethod = useMutation(
    (values: any) => {
      return confirmPaymentMethods(endorsement_body.policySelected?.id, values);
    },
    {
      onError: (err: any) => {
        logAnalytics('PAYMENT_METHOD_CHANGE_FAILURE');
        if (err?.response?.status === 402) {
          setConfirmDialog(true);
        } else if (
          err?.response?.status === 406 ||
          err?.response?.status === 409
        ) {
          setNotAllowUserModal(true);
        } else {
          setOnRetryModal(true);
        }
      },
      onSuccess: (data: any) => {
        logAnalytics('PAYMENT_METHOD_CHANGE_SUCCESS');
        setOnModalSuccess(true);
      },
    }
  );

  const validateCbuNumberMutate = useMutation(
    () => {
      return validateCbuNumber(values.cbu);
    },
    {
      onError: (err: any) => {
        //logAnalytics('PAYMENT_METHOD_CHANGE_FAILURE');
        setNotValid(true);
      },
      onSuccess: (data: any) => {
        //logAnalytics('PAYMENT_METHOD_CHANGE_SUCCESS');
        //setOnModalSuccess(true);
        setCbuValidated(data.data.result);
        if (isCreatingEndorsement === true) {
          if (data.data.result.valid === true) {
            finishEndorsement();
          } else {
            setNotValid(true);
          }
        }
      },
    }
  );

  const creditCardValidation = useMutation(
    () => {
      return validateCreditCardNumber(values.credit_card_number);
    },
    {
      onError: (err: any) => {
        // logAnalytics('PAYMENT_METHOD_CHANGE_FAILURE');
        setNotValid(true);
      },
      onSuccess: (data: any) => {
        // logAnalytics('PAYMENT_METHOD_CHANGE_SUCCESS');
        setCreditCardValidated({ ...data.data.result, isValidated: true });
        if (isCreatingEndorsement === true) {
          if (data.data.result.valid === true) {
            finishEndorsement();
          } else {
            setNotValid(true);
          }
        }
      },
    }
  );

  const maxDate = () => {
    let date = new Date(Date.now());
    date.setDate(date.getDate() + 6000);
    return date;
  };

  const minDate = () => {
    let date = new Date(Date.now());
    date.setDate(date.getDate() - 89);
    return date;
  };

  const savePaymentMethod = () => {
    setIsCreatingEndorsement(true);
    setDialog(true);
  };

  const onSubmit = () => {
    dataLayerPush('cuenta_modificar_pago_solicitada', user?.id);
    logAnalytics('PAYMENT_METHOD_CHANGE_CONFIRM');
    trackEvent(events.ADVICE_MODIFICATION_PAYMENT_METHOD);
    setDialog(false);
    if (values.payment_methods === 'CreditCard') {
      creditCardValidation.mutate(values.credit_card_number);
    } else if (values.payment_methods === 'BankingDebt') {
      validateCbuNumberMutate.mutate(values.cbu);
    } else {
      finishEndorsement();
    }
  };

  const finishEndorsement = () => {
    switch (values.payment_methods) {
      case 'BankingDebt':
        EditPaymentMethod.mutate({
          payment_method_selected: 'BankingDebt',
          // banking_debt: values.banking_institutions,
          cbu: values.cbu,
        });
        return;
      case 'CreditCard':
        EditPaymentMethod.mutate({
          payment_method_selected: 'CreditCard',
          credit_card: creditCardValidated?.info?.issuer,
          card_number: values.credit_card_number,
          expiration_month: (selectedDate.getUTCMonth() + 1).toString(),
          expiration_year: selectedDate.getUTCFullYear().toString().slice(-2),
        });
        return;
      case 'Coupon':
        EditPaymentMethod.mutate({
          payment_method_selected: 'Coupon',
        });
        return;
    }
  };

  const { values, errors, handleChange } = useForm(
    onSubmit,
    EditPaymentMethodValidation
  );

  const { embed } = useAuthDataContext();
  const redirectHome = () => {
    embed
      ? window.parent.postMessage({ return: 'overview' }, '*')
      : history.push('/dashboard');
  };

  const finishChanges = () => {
    setConfirmDialog(false);
    redirectHome();
  };

  useEffect(() => {
    if (values?.cbu?.length === 3) {
      validateCbuNumberMutate.mutate();
    } else if (values?.cbu?.length === 22) {
      validateCbuNumberMutate.mutate();
    } else if (values?.cbu?.length < 3) {
      setCbuValidated({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, values.cbu]);

  useEffect(() => {
    if (values?.credit_card_number?.length === 6) {
      creditCardValidation.mutate();
    } else if (
      values?.credit_card_number?.length >= 14 &&
      values?.credit_card_number?.length <= 16
    ) {
      creditCardValidation.mutate();
    } else if (values?.credit_card_number?.length < 6) {
      setCreditCardValidated({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, values.credit_card_number]);

  useEffect(() => {
    if (!paymentMethodsListLoading && paymentMethodsList) {
      setSkip(true);
    }
  }, [paymentMethodsList, paymentMethodsListLoading]);

  const [paymentMethodButton, setPaymentMethodButton] = useState(false);

  const requestChangePaymentMethodButton = useCallback(() => {
    if (values.payment_methods === 'BankingDebt' && values.cbu) {
      setPaymentMethodButton(true);
    } else if (
      values.payment_methods === 'CreditCard' &&
      values.credit_card_number &&
      selectedDate !== null
    ) {
      setPaymentMethodButton(true);
    } else if (values.payment_methods === 'Coupon') {
      setPaymentMethodButton(true);
    } else if (values.payment_methods !== 'Coupon') {
      setPaymentMethodButton(false);
    }
  }, [
    values.payment_methods,
    values.cbu,
    values.credit_card_number,
    selectedDate,
  ]);

  useEffect(() => {
    requestChangePaymentMethodButton();
  }, [
    values.payment_methods,
    values.cbu,
    values.credit_card_number,
    selectedDate,
    requestChangePaymentMethodButton,
  ]);

  const isSuraPolicy = endorsement_body.policySelected?.provider?.company;

  const isFetching = paymentMethodsListLoading;

  useEffect(() => {
    dataLayerPush('cuenta_modificar_pago_editar', user?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box
        display={isFetching || EditPaymentMethod.isLoading ? 'block' : 'none'}
      >
        <Loader action={'Cargando'}></Loader>
      </Box>

      <SharedDialog
        open={onRetryModal}
        onClose={redirectHome}
        content={
          <Box>
            <Typography variant="h6" style={{ marginBottom: '15px' }}>
              Ha ocurrido un error.
            </Typography>
            <Typography>
              ¡Lo sentimos! El cambio no puede procesarse. Te pedimos que
              reintentes en otro momento.
            </Typography>
          </Box>
        }
        actions={
          <Grid container justify="center">
            <Grid item xs={12} sm={6}>
              <Box m={2}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={redirectHome}
                >
                  Entendido
                </Button>
              </Box>
            </Grid>
          </Grid>
        }
      ></SharedDialog>

      <SharedDialog
        open={notAllowUserModal}
        onClose={redirectHome}
        content={
          <Box>
            <Typography variant="h6" style={{ marginBottom: '15px' }}>
              Ha ocurrido un error.
            </Typography>
            {isSuraPolicy === 'sura' ? (
              <Typography>
                ¡Lo sentimos! El cambio no puede procesarse. Por favor
                comunicate con tu productor/asesor para procesar el cambio.
              </Typography>
            ) : (
              <Typography>
                Por favor comunicate con nosotros por Whatsapp de Lunes a
                Viernes de 9 a 18hs. para procesar el cambio.
              </Typography>
            )}
          </Box>
        }
        actions={
          <Grid container justify="center">
            <Grid item xs={12} sm={6}>
              <Box m={2}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={redirectHome}
                >
                  Entendido
                </Button>
              </Box>
            </Grid>
            {isSuraPolicy !== 'sura' && (
              <Grid item xs={12} sm={6}>
                <Box m={2}>
                  <Link
                    href={appConfig?.WHATSAPP_LINK}
                    rel="noopener"
                    target="_blank"
                  >
                    <Button variant="outlined" fullWidth color="secondary">
                      <Box>
                        <img
                          src={WhatsappSrc}
                          alt="Whatsapp"
                          style={{ marginLeft: '15px', marginRight: '15px' }}
                        />
                      </Box>
                      <Typography display="inline">
                        Comunicarme vía Whatsapp
                      </Typography>
                    </Button>
                  </Link>
                </Box>
              </Grid>
            )}
          </Grid>
        }
      ></SharedDialog>

      <SharedDialog
        open={onModalSuccess}
        onClose={redirectHome}
        content={
          <Box>
            <Typography variant="h6" style={{ marginBottom: '15px' }}>
              ¡Excelente!
            </Typography>
            <Typography>
              Todos los cambios fueron guardados con éxito, estos cambios
              entrarán en vigencia a partir de la proxima factura.
            </Typography>
          </Box>
        }
        actions={
          <Grid container justify="center">
            <Grid item xs={12} sm={6}>
              <Box m={2}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={redirectHome}
                >
                  Entendido
                </Button>
              </Box>
            </Grid>
          </Grid>
        }
      ></SharedDialog>

      <SharedDialog
        open={dialog}
        onClose={() => setDialog(false)}
        content={
          <Box>
            <Box>
              <Typography>¿Estás seguro de los cambios?</Typography>
            </Box>
          </Box>
        }
        actions={
          <Grid container justify="flex-end">
            <Grid item xs={12} sm={6}>
              <Box m={2}>
                <Button
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  onClick={() => setDialog(false)}
                >
                  Descartar
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box m={2} display="flex" alignItems="center">
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  onClick={onSubmit}
                >
                  Si, estoy seguro
                </Button>
              </Box>
            </Grid>
          </Grid>
        }
      ></SharedDialog>

      <SharedDialog
        open={confirmDialog}
        onClose={() => setConfirmDialog(false)}
        content={
          <Box>
            <Typography variant="h6">Ha ocurrido un error</Typography>
            <Box my={2}>
              <Typography>
                Los datos ingresados para el nuevo medio de pago no son
                correctos, por favor intentá nuevamente.
              </Typography>
            </Box>
          </Box>
        }
        actions={
          <Grid container justify="flex-end">
            <Grid item xs={12} sm={6}>
              <Box m={2} display="flex" alignItems="center">
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={finishChanges}
                >
                  Entendido
                </Button>
              </Box>
            </Grid>
          </Grid>
        }
      ></SharedDialog>

      <SharedDialog
        open={notValid}
        onClose={() => setNotValid(false)}
        content={
          <Box>
            <Typography variant="h6" style={{ marginBottom: '15px' }}>
              Ha ocurrido un error.
            </Typography>
            {isSuraPolicy === 'sura' ? (
              <>
                <Typography>
                  Los datos ingresados son incorrectos. Verificá y volvé a
                  intentar, o probá con otro medio de pago.
                </Typography>
                <Typography>
                  Recordá que también podés procesar tu solicitud con tu
                  productor/asesor.
                </Typography>
              </>
            ) : (
              <>
                <Typography>
                  Los datos ingresados son incorrectos. Verificá y volvé a
                  intentar, o probá con otro medio de pago.
                </Typography>
                <Typography>
                  Recordá que también podés procesar tu solicitud por Whatsapp
                  de Lunes a Viernes de 9 a 18hs. para procesar el cambio.
                </Typography>
              </>
            )}
          </Box>
        }
        actions={
          <Grid container justify="center">
            <Grid item xs={12} sm={6}>
              <Box m={2}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={redirectHome}
                >
                  Entendido
                </Button>
              </Box>
            </Grid>
            {isSuraPolicy !== 'sura' && (
              <Grid item xs={12} sm={6}>
                <Box m={2}>
                  <Link
                    href={appConfig?.WHATSAPP_LINK}
                    rel="noopener"
                    target="_blank"
                  >
                    <Button variant="outlined" fullWidth color="secondary">
                      <Box>
                        <img
                          src={WhatsappSrc}
                          alt="Whatsapp"
                          style={{ marginLeft: '15px', marginRight: '15px' }}
                        />
                      </Box>
                      <Typography display="inline">
                        Comunicarme vía Whatsapp
                      </Typography>
                    </Button>
                  </Link>
                </Box>
              </Grid>
            )}
          </Grid>
        }
      ></SharedDialog>

      <SubHeader
        content={
          <Box>
            <Typography variant="h6"> Editar medio de pago </Typography>
          </Box>
        }
      ></SubHeader>

      <Box>
        {!isFetching && !paymentMethodsListError && (
          <Grid container justify="center">
            <Grid container justify="center">
              <Grid item xs={11}>
                <Typography style={{ marginTop: '25px' }}>
                  {' '}
                  Modificar medio de pago{' '}
                </Typography>
              </Grid>
            </Grid>

            {/* Medio de pago */}
            <Grid container justify="center">
              <Grid item xs={11}>
                <Box mt={3}>
                  <Box display="flex">
                    <Typography> Póliza </Typography>
                    <Box ml={1}>
                      <Typography display="block">
                        {' '}
                        {endorsement_body?.policySelected?.number}{' '}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex">
                    <Typography> Método de pago actual: </Typography>
                    <Box ml={1}>
                      {endorsement_body?.policySelected?.payment_type ===
                      'credit_card' ? (
                        <Typography display="block">
                          {endorsement_body?.policySelected?.payment_type_name}{' '}
                          terminada en{' '}
                          {endorsement_body?.policySelected?.payment_method}
                        </Typography>
                      ) : (
                        <Typography display="block">
                          {endorsement_body?.policySelected?.payment_type_name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Box my={2} mr={2}>
                  <Typography variant="subtitle1">
                    {' '}
                    Nuevos medios de pago disponibles para esta póliza *{' '}
                  </Typography>
                  <FormControl fullWidth size="small" variant="outlined">
                    <Select
                      onChange={handleChange}
                      id="payment_methods"
                      name="payment_methods"
                      value={values.payment_methods}
                      displayEmpty
                      inputProps={{ 'aria-label': 'Without label' }}
                      defaultValue=""
                    >
                      <MenuItem value="" disabled>
                        {' '}
                        Seleccioná el nuevo medio de pago{' '}
                      </MenuItem>
                      {paymentMethodsList?.map((paymentMethod: any) => (
                        <MenuItem
                          key={paymentMethod.code}
                          value={paymentMethod.code}
                        >
                          {paymentMethod.description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
            </Grid>

            {/* Cuenta bancaria */}
            {values.payment_methods === 'BankingDebt' && (
              <Grid container justify="center">
                <Grid item xs={11}>
                  <Box mb={2} mt={2}>
                    {cbuValidated?.info?.bank && (
                      <Typography variant="subtitle2">
                        {cbuValidated.info.bank}
                      </Typography>
                    )}
                  </Box>
                  <Box mb={2} mt={2}>
                    <Typography variant="subtitle2"> CBU* </Typography>
                    <TextField
                      InputProps={{
                        inputComponent: NumberFormatCustom as any,
                      }}
                      type="number"
                      size="small"
                      error={errorCbu && Boolean(errors['cbu'])}
                      helperText={errorCbu && errors['cbu']}
                      onChange={handleChange}
                      fullWidth={true}
                      name="cbu"
                      id="cbu"
                      placeholder="Ingresá el CBU *"
                      variant="outlined"
                      value={values.cbu}
                      onBlur={() => setErrorCbu(true)}
                    />
                  </Box>
                </Grid>
              </Grid>
            )}

            {/* Cupon de pago */}
            {values.payment_methods === 'Coupon' && (
              <Grid container justify="center">
                <Grid item xs={11}>
                  <Typography style={{ fontWeight: 'bold' }}>
                    Te enviaremos el cupón de pago por mail
                  </Typography>
                </Grid>
              </Grid>
            )}

            {/* Tarjeta de credito */}
            {values.payment_methods === 'CreditCard' && (
              <>
                <Grid container justify="center">
                  <Grid item xs={11}>
                    <Box mb={2} mt={2}>
                      {creditCardValidated?.info?.issuer && (
                        <Typography variant="subtitle2">
                          {creditCardValidated.info.issuer}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={11}>
                    <Box mb={2} mt={2}>
                      <Typography variant="subtitle2">
                        {' '}
                        Número de tarjeta*{' '}
                      </Typography>
                      <TextField
                        InputProps={{
                          inputComponent: NumberFormatCustom as any,
                        }}
                        type="number"
                        size="small"
                        error={Boolean(errors['credit_card_number'])}
                        helperText={errors['credit_card_number']}
                        onChange={handleChange}
                        fullWidth={true}
                        name="credit_card_number"
                        id="credit_card_number"
                        placeholder="Ingresá el número de tarjeta"
                        variant="outlined"
                        value={values.credit_card_number}
                        required
                      />
                    </Box>
                  </Grid>
                </Grid>

                <Grid container justify="center">
                  <Grid item xs={11}>
                    <Grid container justify="flex-start">
                      <Grid item xs={4}>
                        <Typography
                          variant="subtitle2"
                          style={{ marginBottom: '-10px' }}
                        >
                          Vencimiento
                        </Typography>
                        <MuiPickersUtilsProvider
                          locale={deLocale}
                          utils={DateFnsUtils}
                        >
                          <KeyboardDatePicker
                            views={['month', 'year']}
                            autoOk
                            disablePast
                            placeholder="MM / AA"
                            // refuse={/^(0[1-9]|1[0-2])\/?([0-9]{4}|[0-9]{2})$/}
                            style={{ backgroundColor: 'white' }}
                            variant="inline"
                            inputVariant="outlined"
                            format="MM/yy"
                            margin="normal"
                            id="date-picker"
                            value={selectedDate}
                            onChange={(date) => setSelectedDate(date)}
                            fullWidth={true}
                            maxDate={maxDate()}
                            minDate={minDate()}
                            keyboardIcon={<CalendarIcon />}
                            KeyboardButtonProps={{
                              'aria-label': 'change date',
                            }}
                            invalidLabel={'Formato de fecha inválido'}
                            invalidDateMessage={'Formato de fecha inválido'}
                            InputProps={{ readOnly: true }}
                            helperText={
                              'Seleccioná tu fecha de vencimiento usando el ícono'
                            }
                          />
                        </MuiPickersUtilsProvider>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}

            {/* Boton Solicitar cambio de Medio de Pago */}
            <Grid container justify="center">
              <Grid
                item
                justify="center"
                xs={11}
                style={{ display: 'flex', marginTop: '25px' }}
              >
                <Button
                  variant="contained"
                  onClick={() => savePaymentMethod()}
                  style={{ marginBottom: '5rem' }}
                  disabled={
                    !paymentMethodButton || !!Object.keys(errors).length
                  }
                >
                  Solicitar cambio de medio de pago
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Box>
    </>
  );
};

export default EditPaymentMethod;
